'use strict';
/* ==========================================================================
   levels/print.js — step "print" (orientation any, notebook style): FINE PRINT (DESIGN.md §5.2).

   Six prize posters per attempt from the pool `print` (content/bank.print.json through Chance.pool → core/bank.js:
   draw.n = 6, draw.unique = "cheat" so no cheat twice, draw.require = ["legit"] so exactly one honest poster), served
   easiest first (Chance.byLevel). Every poster is built by the model: Chance.poster(item, params) → the claims as shown,
   each claim's honest value and working (the sample space listed, the favourable ones marked, P = f ÷ n), the bad
   claim's index. Every number on screen comes from it; the experiment is drawn in ink from its outcomes (Chance.exp).
   The follow-up options are the bank instance's (formula + named distractors, exact fractions via chance.ftext).

   A poster drops onto the page: an index card, the title in sale orange, the experiment drawn in the band below it
   (spinner = equal slices clockwise as listed, bag / jar = one marble or gumball per outcome, die = the six faces,
   coin = H and T, streak = the last k flips / rolls, then the next one's outcomes), the 3 claims on the ruled lines.
   Per poster:  drop → spot: tap the wrong claim, or "All correct". A tap on an honest claim is a miss: "That claim is
                     fine: P = 1 ÷ 6 = 1/6." (a pencil tick beside it); after 2 misses Ms. Rivera circles it herself.
                     "÷" beside a claim shows its working for free (in the band beside the drawing; the favourable
                     outcomes highlighted on the drawing).
             → value: the item's q with 3 options; a wrong pick is crossed out with its `why`, pick again
             → fix: Rivera's red pen strikes the wrong value and writes the honest one; her card: the bad claim's
                    working and the item's filled `explain` → next poster.
   Stars: right claim first try = 1 + right value first try = 1 (legit: "All correct" first try = 1 + value = 1). of = 12,
   pass = pool.passStars (8). Game.progress.scores.print = {best, last, of}; first answers under
   Game.progress.answers['print/<key>/claim'] and ['print/<key>']; Bank.finish at the end; the stamp (Game.complete)
   through "Collect your stamp" on a pass (a pass that leaves without collecting keeps it). Resume:
   Game.progress.bank.print.run = {i, ev, res} (the current poster's taps / checks / picks are replayed after a reload).

   Portrait phone (Dock.active): the stage shows the poster large (a picture); claims are Dock.choices, "All correct" a
   dock button, the ÷ checks dock buttons, the value options Dock.choices with the why as notes, the fix a list.
   Landscape phone: the claims / ÷ are canvas tap targets ≥ 44 CSS px (UI.tap).
   Keys: 1–3 = claim n, A = All correct, 1–3 = option, Enter = next.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'value' | 'fix' | 'end'), sc.poster (the Chance.poster result),
   sc.answer() (the bad claim index or 'legit'), sc.tapClaim(i), sc.legit(), sc.choose(i), sc.check(i), sc.next(),
   sc.begin(fresh), sc.collect(), sc.back(); sc.inst, sc.instances, sc.bi, sc.results, sc.stars, sc.seed, sc.attempt,
   sc.resumed, sc.misses, sc.picks, sc.marks, sc.checked; Levels.print.solution ({legit, claim, answer, value,
   valueText}) and Levels.print.claims() (the tap rects of the live poster's claims on the canvas).
   ========================================================================== */
(function () {
  const STEP = 'print';
  const MAX_MISSES = 2, DROP = 0.7, NEXT_GUARD = 0.3;
  const pool = () => {
    const p = Bank.pool(STEP);
    return p && p.items.length ? p : Bank.load(Chance.pool(Game.content.bank_print || { pool: STEP, draw: { n: 0 }, items: [] }));
  };
  const SHORT = { equiprobable: 'every prize "equally likely"', partToPart: 'part against part', gamblersFallacy: '"due" after a streak',
    smallSample: 'a few tries as the truth', complement: 'P(not A) mixed up', conversion: 'a conversion slip', legit: 'all correct' };
  const INK = () => NB.PAL.ink, RED = () => NB.PAL.redpen, PENCIL = () => NB.PAL.pencil;
  const uiF = (s, w = 400) => `${w} ${s}px ${NB.FONT.ui}`, handF = (s, w = 400) => `${w} ${s}px ${NB.FONT.hand}`;
  const tw = (s, f) => { g.save(); g.font = f; const w = g.measureText(s).width; g.restore(); return w; };
  const prog = (t0, d) => Math.max(0, Math.min(1, (T - t0) / d));
  const last = a => a[a.length - 1];

  /* one poster: the model's poster + the bank instance */
  function round(inst) {
    const P = Chance.poster(inst.item, inst.params), src = Chance.source(inst.item);
    const jar = /jar|gumball/i.test(src.id + ' ' + (src.claims || []).map(c => c.text).join(' '));
    // a jar's working says "In the jar" (the model's noun for a bag is "In the bag"; the numbers are untouched)
    if (jar) P.claims.forEach(c => { c.working = { ...c.working, lines: c.working.lines.map(l => l.replace(/^In the bag:/, 'In the jar:')) }; });
    return { inst, P, src, legit: P.cheat === 'legit', bad: P.bad, n: P.claims.length, jar };
  }
  const pctQ = q => /percent/i.test(q.text || '');
  const optText = (q, i) => (pctQ(q) ? q.options[i] + '%' : 'P = ' + q.options[i]);
  /* the claim's last working line (the one-line reason a claim is fine) */
  const oneLine = c => last(c.working.lines);
  /* where the claimed value starts in a claim's text (the red pen strikes from here to the end) */
  function valueAt(text) {
    const s = String(text), at = s.lastIndexOf('P(');
    if (at >= 0) {
      let depth = 0, j = at + 1;
      for (; j < s.length; j++) { if (s[j] === '(') depth++; else if (s[j] === ')' && --depth === 0) break; }
      const m = /^\s*(=|is)\s*/.exec(s.slice(j + 1));
      if (m) return j + 1 + m[0].length;
    }
    const m = /[\d.,/%]+\s*$/.exec(s);
    return m ? m.index : 0;
  }

  /* ---------------- the experiment in ink ---------------- */
  const COLORS = { RED: '#e0564c', BLUE: '#5b86d6', GREEN: '#5bb46e', WHITE: '#ffffff', GOLD: '#e8bf45', SILVER: '#c7cbd3', YELLOW: '#f2d64b', BLACK: '#3a3d46', PURPLE: '#9a6bc9', ORANGE: '#ef8a3a', PINK: '#f09bb5' };
  function initials(labels) {
    const out = {}; let k = 1;
    while (k < 4) { labels.forEach(l => { out[l] = l.replace(/[^A-Z0-9%]/gi, '').slice(0, k).toUpperCase(); }); if (new Set(Object.values(out)).size === labels.length) break; k++; }
    return out;
  }
  /* a group's ink pattern inside the current clip (0 plain, 1 hatch, 2 dots, 3 cross-hatch, 4 lines) */
  function pattern(gi, bx, by, bw, bh, lw) {
    const p = gi % 5;
    if (!p) return;
    g.save(); g.strokeStyle = INK(); g.fillStyle = INK(); g.lineWidth = lw; g.globalAlpha *= 0.7;
    const step = 16;
    if (p === 2) { for (let yy = by; yy < by + bh; yy += step) for (let xx = bx + ((yy - by) / step % 2) * step / 2; xx < bx + bw; xx += step) { g.beginPath(); g.arc(xx, yy, lw * 1.1, 0, TAU); g.fill(); } }
    else {
      g.beginPath();
      const dirs = p === 1 ? [1] : p === 3 ? [1, -1] : [0];
      for (const d of dirs) for (let k = -bh; k < bw + bh; k += step) {
        if (d === 0) { if (k >= 0 && k < bh) { g.moveTo(bx, by + k); g.lineTo(bx + bw, by + k); } }
        else if (d > 0) { g.moveTo(bx + k, by); g.lineTo(bx + k - bh, by + bh); }
        else { g.moveTo(bx + k - bh, by); g.lineTo(bx + k, by + bh); }
      }
      g.stroke();
    }
    g.restore();
  }
  function hiFill(path, alpha = 0.8) {
    g.save(); if (window.Quality && Quality.tier !== 'low') g.globalCompositeOperation = 'multiply';
    g.globalAlpha *= alpha; g.fillStyle = NB.PAL.hi; path(); g.fill(); g.restore();
  }
  function spinnerAt(e, cx, cy, r, fav, lw) {
    const n = e.n, a0 = -Math.PI / 2;
    e.outcomes.forEach((o, k) => {
      const s = a0 + k * TAU / n, t = a0 + (k + 1) * TAU / n;
      const slice = () => { g.beginPath(); g.moveTo(cx, cy); g.arc(cx, cy, r, s, t); g.closePath(); };
      g.save(); slice(); g.fillStyle = '#fff'; g.fill(); g.clip(); pattern(o.group, cx - r, cy - r, 2 * r, 2 * r, lw * 0.5); g.restore();
      if (fav && fav[k]) hiFill(slice, 0.85);
    });
    g.save(); g.strokeStyle = INK(); g.lineWidth = lw; g.lineCap = 'round';
    g.beginPath(); for (let k = 0; k < n; k++) { const s = a0 + k * TAU / n; g.moveTo(cx, cy); g.lineTo(cx + Math.cos(s) * r, cy + Math.sin(s) * r); } g.stroke();
    g.lineWidth = lw * 1.4; g.beginPath(); g.arc(cx, cy, r, 0, TAU); g.stroke();
    g.fillStyle = INK(); g.beginPath(); g.arc(cx, cy, lw * 2.2, 0, TAU); g.fill();
    // the pointer at the top
    g.beginPath(); g.moveTo(cx - r * 0.1, cy - r - r * 0.2); g.lineTo(cx + r * 0.1, cy - r - r * 0.2); g.lineTo(cx, cy - r + r * 0.08); g.closePath();
    g.fillStyle = NB.PAL.redpen; g.fill(); g.lineWidth = lw * 0.7; g.stroke();
    g.restore();
  }
  function legend(e, x, y, w, fs, lw) {
    const rows = e.groups.map((gr, gi) => ({ gi, label: gr.label }));
    const sw = fs * 1.05, lh = fs * 1.4;
    rows.forEach((r, k) => {
      const yy = y + k * lh;
      g.save(); g.beginPath(); g.rect(x, yy - sw / 2, sw, sw); g.fillStyle = '#fff'; g.fill(); g.clip(); pattern(r.gi, x, yy - sw / 2, sw, sw, lw * 0.5); g.restore();
      g.save(); g.strokeStyle = INK(); g.lineWidth = lw * 0.7; g.strokeRect(x, yy - sw / 2, sw, sw); g.restore();
      NB.write(r.label, x + sw + 12, yy, { font: 'ui', weight: 700, size: fs, color: INK() });
    });
    return rows.length * lh;
  }
  function marble(o, x, y, r, fav, ini, lw) {
    if (fav) { g.save(); g.fillStyle = NB.PAL.hi; g.beginPath(); g.arc(x, y, r * 1.38, 0, TAU); g.fill(); g.restore(); }
    g.save();
    g.beginPath(); g.arc(x, y, r, 0, TAU); g.fillStyle = '#fff'; g.fill();
    const c = COLORS[String(o.label).toUpperCase()];
    if (c) { g.globalAlpha *= 0.6; g.fillStyle = c; g.fill(); g.globalAlpha /= 0.6; }
    g.lineWidth = lw; g.strokeStyle = INK(); g.stroke();
    g.font = uiF(Math.round(r * (ini.length > 1 ? 0.8 : 1.05)), 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(ini, x, y + r * 0.06);
    g.restore();
  }
  /* a bag (drawstring) or a jar (glass, lid) holding one marble per outcome */
  function holder(R, e, box, fav, lw) {
    const jar = R.jar, n = e.n, ini = initials(e.labels);
    const legendH = 0;
    const bw = Math.min(box.w * 0.92, box.h * 1.05), bh = box.h - legendH - 8;
    const bx = box.x + (box.w - bw) / 2, by = box.y + 4;
    g.save(); g.strokeStyle = INK(); g.lineWidth = lw * 1.2; g.lineJoin = 'round'; g.lineCap = 'round';
    let ix, iy, iw, ih;
    if (jar) {
      const lidH = bh * 0.1, top = by + lidH + 6;
      g.fillStyle = '#fff'; g.beginPath(); g.roundRect ? g.roundRect(bx, top, bw, bh - lidH - 6, [14, 14, bw * 0.16, bw * 0.16]) : g.rect(bx, top, bw, bh - lidH - 6); g.fill(); g.stroke();
      g.fillStyle = '#f3f1e6'; g.beginPath(); g.rect(bx + bw * 0.12, by, bw * 0.76, lidH); g.fill(); g.stroke();
      g.globalAlpha *= 0.5; g.lineWidth = lw * 0.8; g.beginPath(); g.moveTo(bx + bw * 0.08, top + 26); g.lineTo(bx + bw * 0.08, top + (bh - lidH) * 0.55); g.stroke(); g.globalAlpha /= 0.5;
      ix = bx + bw * 0.14; iy = top + 16; iw = bw * 0.76; ih = bh - lidH - 6 - 30;
    } else {
      const neck = by + bh * 0.16;
      g.fillStyle = '#f3f1e6';
      g.beginPath();
      g.moveTo(bx + bw * 0.34, neck);
      g.bezierCurveTo(bx - bw * 0.02, neck + bh * 0.12, bx - bw * 0.04, by + bh, bx + bw * 0.2, by + bh);
      g.lineTo(bx + bw * 0.8, by + bh);
      g.bezierCurveTo(bx + bw * 1.04, by + bh, bx + bw * 1.02, neck + bh * 0.12, bx + bw * 0.66, neck);
      g.closePath(); g.fill(); g.stroke();
      // the gathered top and the string
      g.beginPath(); g.moveTo(bx + bw * 0.34, neck); g.lineTo(bx + bw * 0.28, by + 6); g.lineTo(bx + bw * 0.4, by + bh * 0.08); g.lineTo(bx + bw * 0.5, by + 2); g.lineTo(bx + bw * 0.6, by + bh * 0.08); g.lineTo(bx + bw * 0.72, by + 6); g.lineTo(bx + bw * 0.66, neck); g.stroke();
      g.lineWidth = lw; g.beginPath(); g.moveTo(bx + bw * 0.3, neck + 4); g.quadraticCurveTo(bx + bw * 0.5, neck + 16, bx + bw * 0.7, neck + 4); g.stroke();
      ix = bx + bw * 0.12; iy = neck + bh * 0.1; iw = bw * 0.76; ih = by + bh - iy - 14;
    }
    g.restore();
    // the marbles: a grid in the holder, grouped as listed
    let cols = 1, best = 0;
    for (let c = 1; c <= n; c++) { const cl = Math.min(iw / c, ih / Math.ceil(n / c)); if (cl > best + 0.01) { best = cl; cols = c; } }
    const rows = Math.ceil(n / cols);
    const cell = Math.min(iw / cols, ih / rows), r = Math.min(cell * 0.4, 46);
    const gx = ix + (iw - cols * cell) / 2, gy = iy + (ih - rows * cell) / 2;
    e.outcomes.forEach((o, k) => {
      const row = Math.floor(k / cols), col = k % cols;
      const inRow = row === rows - 1 ? n - row * cols : cols, off = (cols - inRow) * cell / 2;
      marble(o, gx + off + (col + 0.5) * cell, gy + (row + 0.5) * cell, r, fav && fav[k], ini[o.label], lw * 0.8);
    });
  }
  function dieFace(v, x, y, s, fav, lw) {
    if (fav) { g.save(); g.fillStyle = NB.PAL.hi; g.beginPath(); g.roundRect ? g.roundRect(x - s * 0.62, y - s * 0.62, s * 1.24, s * 1.24, s * 0.26) : g.rect(x - s * 0.62, y - s * 0.62, s * 1.24, s * 1.24); g.fill(); g.restore(); }
    g.save(); g.fillStyle = '#fff'; g.strokeStyle = INK(); g.lineWidth = lw;
    g.beginPath(); g.roundRect ? g.roundRect(x - s / 2, y - s / 2, s, s, s * 0.18) : g.rect(x - s / 2, y - s / 2, s, s); g.fill(); g.stroke();
    const P = { 1: [[0, 0]], 2: [[-1, -1], [1, 1]], 3: [[-1, -1], [0, 0], [1, 1]], 4: [[-1, -1], [1, -1], [-1, 1], [1, 1]], 5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]], 6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]] }[v] || [];
    g.fillStyle = INK();
    for (const [a, b] of P) { g.beginPath(); g.arc(x + a * s * 0.26, y + b * s * 0.26, s * 0.085, 0, TAU); g.fill(); }
    g.restore();
  }
  function coinAt(face, x, y, r, fav) {
    if (fav) { g.save(); g.fillStyle = NB.PAL.hi; g.beginPath(); g.arc(x, y, r * 1.3, 0, TAU); g.fill(); g.restore(); }
    if (Levels.title && Levels.title.coin) Levels.title.coin(x, y, r, { face });
    else { g.save(); g.fillStyle = '#f3f1e6'; g.strokeStyle = INK(); g.lineWidth = 5; g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill(); g.stroke(); g.font = handF(r, 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(face, x, y); g.restore(); }
  }
  /* the sample space as tokens (die faces, coins, tiles, cards) in a box */
  function spaceTokens(e, box, fav, lw) {
    const n = e.n;
    if (e.kind === 'die') {
      const s = Math.min(box.w / 3.7, box.h / 2.5);
      e.outcomes.forEach((o, k) => dieFace(o.value, box.x + box.w / 2 + ((k % 3) - 1) * s * 1.3, box.y + box.h / 2 + (k < 3 ? -0.65 : 0.65) * s, s, fav && fav[k], lw));
      return;
    }
    if (e.kind === 'coin') {
      const r = Math.min(box.w / 5.2, box.h / 2.6);
      e.outcomes.forEach((o, k) => coinAt(o.label, box.x + box.w / 2 + (k ? 1 : -1) * r * 1.35, box.y + box.h / 2, r, fav && fav[k]));
      return;
    }
    const cols = Math.min(n, Math.max(1, Math.ceil(Math.sqrt(n * box.w / box.h)))), rows = Math.ceil(n / cols), cell = Math.min(box.w / cols, box.h / rows), s = cell * 0.8;
    e.outcomes.forEach((o, k) => {
      const x = box.x + (box.w - cols * cell) / 2 + (k % cols + 0.5) * cell, y = box.y + (box.h - rows * cell) / 2 + (Math.floor(k / cols) + 0.5) * cell;
      if (fav && fav[k]) { g.save(); g.fillStyle = NB.PAL.hi; g.fillRect(x - s * 0.6, y - s * 0.6, s * 1.2, s * 1.2); g.restore(); }
      g.save(); g.fillStyle = '#fff'; g.strokeStyle = INK(); g.lineWidth = lw; g.fillRect(x - s / 2, y - s / 2, s, s); g.strokeRect(x - s / 2, y - s / 2, s, s);
      g.font = handF(Math.round(s * 0.55), 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(o.label, x, y + 2); g.restore();
    });
  }
  /* the streak before the next flip / roll: k tails, or k rolls without the missing face (values from a fixed seed) */
  function streakRow(R, e, box, lw, fs) {
    const pr = R.P.params, k = Math.max(1, Math.min(20, +pr.k || 3)), die = e.n === 6, miss = +pr.s || 6;
    NB.write(die ? `the last ${k} rolls: no ${miss}` : `the last ${k} flips: all tails`, box.x + box.w / 2, box.y + fs * 0.6, { font: 'ui', weight: 700, size: fs, color: PENCIL(), align: 'center' });
    const perRow = Math.min(k, die ? 6 : 6), rows = Math.ceil(k / perRow), top = box.y + fs * 1.4, h = box.h - fs * 1.4;
    const cell = Math.min(box.w / perRow, h / rows), s = cell * 0.74;
    const rng = Chance.rng('print-streak:' + R.P.id + ':' + k), faces = [1, 2, 3, 4, 5, 6].filter(v => v !== miss);
    for (let i = 0; i < k; i++) {
      const row = Math.floor(i / perRow), col = i % perRow, inRow = row === rows - 1 ? k - row * perRow : perRow;
      const x = box.x + (box.w - inRow * cell) / 2 + (col + 0.5) * cell, y = top + (h - rows * cell) / 2 + (row + 0.5) * cell;
      if (die) dieFace(faces[rng.int(faces.length)], x, y, s, false, lw * 0.7); else coinAt('T', x, y, s / 2, false);
    }
  }
  /* the whole experiment in its box: fav = the favourable flags of the claim whose working is shown (or null) */
  function drawExp(R, box, fav, docked) {
    const e = R.P.exp, lw = docked ? 5 : 3.5, fs = docked ? 34 : Math.round(Math.min(30, UI.text(22)));
    if (e.kind === 'spinner') {
      const lh = fs * 1.4, legH = e.groups.length * lh;
      const legW = Math.max(...e.groups.map(gr => tw(gr.label, uiF(fs, 700)))) + fs * 1.05 + 12;
      if (box.w - legW - 40 > box.h * 0.9) {                                   // wide box: the legend beside the wheel
        const r = Math.max(40, Math.min(box.h / 2.3, (box.w - legW - 70) / 2)), cx = box.x + r + 30, cy = box.y + r * 1.2 + 4;
        spinnerAt(e, cx, cy, r, fav, lw);
        legend(e, cx + r + 30, cy - legH / 2 + lh / 2, box.w, fs, lw);
      } else {
        const r = Math.max(40, Math.min(box.w / 2 - 12, (box.h - legH - 22) / 2.2));
        spinnerAt(e, box.x + box.w / 2, box.y + r * 1.2 + 4, r, fav, lw);
        legend(e, box.x + (box.w - legW) / 2, box.y + r * 2.2 + 18 + lh / 2, box.w, fs, lw);
      }
    } else if (e.kind === 'bag') holder(R, e, box, fav, lw);
    else if (e.streak) {
      const top = { x: box.x, y: box.y, w: box.w, h: box.h * 0.46 };
      streakRow(R, e, top, lw, fs);
      const ny = box.y + box.h * 0.5;
      NB.write('the next one:', box.x + box.w / 2, ny + fs * 0.5, { font: 'ui', weight: 700, size: fs, color: PENCIL(), align: 'center' });
      spaceTokens(e, { x: box.x, y: ny + fs * 1.1, w: box.w, h: box.h * 0.5 - fs * 1.1 }, fav, lw);
    } else spaceTokens(e, box, fav, lw);
  }

  /* ---------------- layouts (mode: 'm' mouse / desktop, 't' touch landscape, 'd' the portrait stage picture) ---------------- */
  function posterLay(R, mode) {
    const n = R.n, docked = mode === 'd';
    let x, y, w, head, band, rowH;
    if (docked) { x = 40; y = 16; w = 1840; head = 140; band = 540; rowH = Math.floor((1064 - y - head - band - 16) / n); }
    else { x = mode === 't' ? 150 : 180; y = 205; w = mode === 't' ? 940 : 900; head = 100; band = 340; rowH = mode === 't' ? Math.max(110, Math.ceil(UI.tap(64)) + 8) : 110; }
    const h = head + band + n * rowH + 16;
    if (!docked && y + h > 1068) y = Math.max(92, 1068 - h);
    const r = docked ? 0 : UI.tap(52) / 2;
    const numX = x + (docked ? 56 : 42), tx = x + (docked ? 110 : 78), reserve = docked ? 250 : 150;
    const base = docked ? 62 : Math.round(UI.text(38));
    const widest = Math.max(...R.P.claims.map(c => tw(c.text, handF(base, 700))));
    const size = Math.max(docked ? 34 : 22, Math.floor(Math.min(base, base * (x + w - 24 - reserve - tx) / Math.max(1, widest))));
    const rows = R.P.claims.map((c, i) => { const top = y + head + band + i * rowH; return { i, top, h: rowH, cy: top + rowH / 2, cx: x + w / 2, w }; });
    const bandTop = y + head;
    const dw = docked ? 760 : 400;
    const dbox = { x: x + 24, y: bandTop + 14, w: dw, h: band - 28 };
    const wx = dbox.x + dbox.w + (docked ? 50 : 30), wbox = { x: wx, y: bandTop + 18, w: x + w - 24 - wx, h: band - 36 };
    const chkX = x + w + 16 + r;
    return { mode, x, y, w, h, head, band, rowH, rows, r, numX, tx, size, dbox, wbox, chkX, panelX: docked ? 1900 : Math.max(1170, chkX + r + 34) };
  }

  class Print extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : this.pool.draw.n; }
    get of() { return this.n * 2; }
    get passStars() { const j = this.pool.json || {}; return Math.min(this.of, j.passStars || Math.ceil(this.of * 2 / 3)); }
    get inst() { return this.R ? this.R.inst : null; }
    get poster() { return this.R ? this.R.P : null; }
    get state() { return this.phase; }
    get solution() { const R = this.R; return R ? { legit: R.legit, claim: R.bad, answer: R.legit ? 'legit' : R.bad, value: R.inst.answerIndex, valueText: R.inst.options[R.inst.answerIndex] } : null; }
    answer() { return this.R ? (this.R.legit ? 'legit' : this.R.bad) : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }

    enter() {
      this.phase = 'intro'; this.instances = null; this.R = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, pool(), { fresh });
      this.instances = Chance.byLevel(a.instances);                                  // easiest first (stable)
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startPoster(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.instances.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.boardAt = Game.time - DROP - 1; this.phase = 'spot'; this.landed = true;
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 't') this.tapClaim(v); else if (k === 'l') this.legit(); else if (k === 'c') this.choose(v); else if (k === 'k') this.check(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[print] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.R = round(this.instances[i]); this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {}; this.checked = {}; this.lastCheck = -1; this.found = -1; this.revealed = false;
      this.picks = []; this.say = null; this.claimStar = 0; this.landed = false; this._fc = null;
      this.boardAt = Game.time; this.foundAt = this.pAt = this.fixAt = this.pickedAt = null; this.lastCheckAt = -1e9;
    }
    startPoster(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.35 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && (!Audio.has || Audio.has(name))) Audio.play(name, o); }
    pen() { this.sfx(Audio.has && Audio.has('pen') ? 'pen' : 'scribble', { gain: 0.45 }); }

    /* ---- ÷: a claim's working, free (never an answer, never costs a star) ---- */
    check(i) {
      const R = this.R;
      if (!R || !(i >= 0 && i < R.n) || this.phase === 'intro' || this.phase === 'end') return false;
      if (this.phase === 'drop') this.phase = 'spot';
      this.ev.push(['k', i]);
      this.checked[i] = this.checked[i] || { at: Game.time };
      this.lastCheck = i; this.lastCheckAt = Game.time;
      this.sfx(Audio.has && Audio.has('beep') ? 'beep' : 'pop', { gain: 0.3 });
      this.save();
      return true;
    }
    working(i) { return this.R ? this.R.P.claims[i].working : null; }

    /* ---- spot the wrong claim ---- */
    tapClaim(i) {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R || !(i >= 0 && i < R.n) || this.marks[i]) return false;
      this.ev.push(['t', i]);
      Game.progress.answer(STEP + '/' + R.inst.key + '/claim', R.P.claims[i].text);   // first try only
      if (!R.legit && i === R.bad) { this.claimStar = this.misses === 0 ? 1 : 0; this.foundClaim(i, false); this.save(); return true; }
      this.marks[i] = { at: Game.time };
      this.checked[i] = this.checked[i] || { at: Game.time };
      this.lastCheck = i; this.lastCheckAt = Game.time;
      this.miss(`That claim is fine: ${oneLine(R.P.claims[i])}.`);
      this.save();
      return true;
    }
    legit() {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R) return false;
      this.ev.push(['l']);
      Game.progress.answer(STEP + '/' + R.inst.key + '/claim', 'All correct');       // first try only
      if (R.legit) { this.claimStar = this.misses === 0 ? 1 : 0; this.toValue(); this.sfx('ding', { gain: 0.6 }); this.save(); return true; }
      this.miss('Not all correct: one claim is wrong. Check each one against the drawing.');
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time };
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        const R = this.R;
        this.claimStar = 0;
        if (R.legit) { this.say = { text: 'Every claim checks out: this poster is all correct.', at: Game.time }; this.revealed = true; this.toValue(); }
        else { this.say = { text: `Ms. Rivera circles it: ${oneLine(R.P.claims[R.bad])}, not ${R.P.claims[R.bad].shownText}.`, at: Game.time }; this.foundClaim(R.bad, true); }
      }
    }
    foundClaim(i, revealed) {
      this.found = i; this.revealed = revealed; this.foundAt = Game.time;
      this.checked[i] = this.checked[i] || { at: Game.time };
      if (!revealed) this.say = null;
      this.pen();
      this.toValue(0.6);
    }
    toValue(delay = 0.4) { this.phase = 'value'; this.pAt = Game.time + delay; }

    /* ---- the real value ---- */
    choose(i) {
      const q = this.inst;
      if (this.phase !== 'value' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);   // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (Bank.check(q, i)) {
        const vStar = this.picks.length === 1 ? 1 : 0;
        this.sfx('ding', { gain: 0.7 });
        this.toFix(this.claimStar, vStar);
      } else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(claimStar, valueStar) {
      const R = this.R;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null;
      this.results[this.bi] = { id: R.inst.id, key: R.inst.key, cheat: R.P.cheat, legit: R.legit, title: R.P.title, misses: this.misses,
        revealed: this.revealed, wrongPicks: this.picks.length - 1, claimStar, valueStar, stars: claimStar + valueStar };
      this.pen();
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.instances.length - 1) this.startPoster(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                             // the next attempt draws new posters
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new posters)', go: () => this.begin() }]
        : [{ label: 'Try again (new posters)', go: () => this.begin() }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.boardAt > DROP + 0.1) this.phase = 'spot';
      if (this.R && !this.landed && Game.time - this.boardAt > DROP * 0.7) { this.landed = true; this.sfx('paper', { gain: 0.45, rate: 1.1 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') { if (n >= 1 && n <= 9) this.tapClaim(n - 1); else if (k === 'a' || k === 'A') this.legit(); }
      else if (this.phase === 'value') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm', key = mode + '|' + Math.round(UI.tap(64)) + '|' + this.bi;
      let L = this.lays.get(key);
      if (!L) { L = posterLay(this.R, mode); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.instances || this.phase === 'intro' || this.phase === 'end') return '';
      return `poster ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      NB.ground();
      UI.hud('Fine Print', this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked), R = this.R;
      const s = E.outBack(Math.max(0, Math.min(1, (T - this.boardAt) / DROP)), 1.1);
      g.save(); g.translate(0, (1 - s) * -1000); g.rotate((1 - s) * -0.05);
      this.drawPoster(R, L, this.posterState(docked));
      g.restore();
      if (docked) return this.drawDock(L);
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot(L);
      else if (this.phase === 'value') this.drawValue(L);
      else this.drawFix(L);
    }
    posterState(docked) {
      const spot = this.phase === 'spot' || this.phase === 'drop', fix = this.phase === 'fix';
      // whose working the band shows: the last checked claim; in the fix, the bad claim (legit: the last checked)
      const w = fix && !this.R.legit ? this.R.bad : this.lastCheck;
      return { docked, spot, live: this.phase === 'spot' && !docked, marks: this.marks, checked: this.checked, found: this.found, foundAt: this.foundAt,
        fix, fixAt: this.fixAt, bi: this.bi, keys: UI.keys && spot && !docked, show: w, showAt: fix && !this.R.legit ? this.fixAt : this.lastCheckAt,
        chips: !docked && (spot || this.phase === 'value') };
    }

    /* ---- the poster: an index card, the title, the experiment + the working, the claims ---- */
    drawPoster(R, L, st) {
      const P = R.P, { x, y, w, h, head, band, rowH, size, tx, numX } = L, docked = L.mode === 'd';
      NB.indexCard(x, y, w, h, { lines: false });
      g.save();
      g.fillStyle = RED(); g.fillRect(x, y + head - 1, w, 2.5);
      g.fillStyle = NB.PAL.cardLine;
      for (let i = 0; i <= R.n; i++) g.fillRect(x, y + head + band + i * rowH - 1, w, 1.5);
      g.restore();
      const ts = Math.round(head * 0.52);
      NB.write(P.title, x + 30, y + head * 0.52, { size: ts, weight: 700, color: NB.PAL.sale });
      NB.write('the fine print ↓', x + w - 30, y + head * 0.55, { font: 'ui', size: Math.round(head * 0.22), color: PENCIL(), align: 'right' });
      // the experiment, favourable outcomes highlighted for the claim whose working is shown
      const show = st.show >= 0 ? P.claims[st.show] : null;
      drawExp(R, L.dbox, show ? show.working.fav : null, docked);
      this.drawWorking(R, L, st, show);
      // the claims on the ruled lines
      for (const row of L.rows) {
        const c = P.claims[row.i];
        if (st.live) {
          const hit = UI._hit('fp-row' + st.bi + '_' + row.i, row.cx, row.cy, w, row.h);
          if (hit.hover && !st.marks[row.i]) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(x + 6, row.top + 4, w - 12, row.h - 8); g.restore(); }
          if (hit.clicked) this.tapClaim(row.i);
        }
        const faded = st.marks[row.i];
        NB.write(String(row.i + 1), numX, row.cy, { font: 'ui', weight: 700, size: Math.round(size * 0.7), color: PENCIL(), align: 'center' });
        const cw = NB.write(c.text, tx, row.cy, { size, weight: 700, color: faded ? PENCIL() : INK() });
        if (faded) NB.tick(Math.min(tx + cw + 40, x + w - 30), row.cy, { s: docked ? 1.3 : 0.8, color: PENCIL(), t0: faded.at });
        else if (st.checked[row.i] && !st.fix && !(st.found === row.i)) NB.write('✓', Math.min(tx + cw + 26, x + w - 26), row.cy - size * 0.3, { font: 'ui', size: Math.round(size * 0.55), color: PENCIL() });
        if (st.chips && UI.iconButton('fp-chk' + st.bi + '_' + row.i, '÷', L.chkX, row.cy, { r: L.r, on: st.show === row.i })) this.check(row.i);
      }
      if (st.found >= 0) { const row = L.rows[st.found], c = P.claims[st.found], cw = tw(c.text, handF(size, 700)); NB.circle(tx + cw / 2, row.cy, cw / 2 + 28, rowH * 0.42, { t0: st.foundAt, dur: 0.4 }); }
      if (!st.fix) return;
      const t0 = st.fixAt + 0.35;
      if (R.legit) { L.rows.forEach((row, k) => NB.tick(x + w - (docked ? 70 : 44), row.cy, { s: docked ? 1.5 : 1, t0: t0 + k * 0.12 })); return; }
      // Rivera's red pen: strike the claimed value, write the honest one after it
      const row = L.rows[R.bad], c = P.claims[R.bad], f = handF(size, 700);
      const a = valueAt(c.text), x0 = tx + tw(c.text.slice(0, a), f), x1 = tx + tw(c.text, f);
      NB.strike(x0 - 6, row.cy + size * 0.2, x1 + 8, row.cy - size * 0.2, { t0, w: docked ? 8 : 6 });
      const honest = /[%.]/.test(c.text.slice(a)) && Chance.isProbEvent(c.event) ? `${c.honestText} = ${Chance.decpct(c.honest)}` : c.honestText;
      let ns = docked ? 64 : Math.round(Math.min(46, UI.text(40)));
      const room = x + w - 20 - (x1 + 44);
      const nw = tw(honest, handF(ns, 700));
      if (nw <= room) NB.note(honest, x1 + 44, row.cy, -0.03, { t0: t0 + 0.3, size: ns, weight: 700 });
      else { ns = Math.max(24, Math.min(ns, Math.floor(ns * (x + w - 30 - x0) / nw))); NB.note(honest, Math.max(tx, Math.min(x0, x + w - 20 - tw(honest, handF(ns, 700)))), row.top + ns * 0.45, -0.02, { t0: t0 + 0.3, size: ns, weight: 700 }); }
    }
    /* the band beside the drawing: the working of the claim shown (or a pencil hint) */
    drawWorking(R, L, st, show) {
      const B = L.wbox, docked = L.mode === 'd';
      if (!show) {
        if (!st.spot && this.phase !== 'value') return;
        const fs = docked ? 38 : Math.round(Math.min(28, UI.text(24)));
        const lines = UI._wrap(docked ? 'Tap ÷ below a claim number to see its working (free).' : 'Tap ÷ beside a claim to see its working. Free: it never costs a star.', B.w, uiF(fs));
        lines.forEach((l, k) => NB.write(l, B.x, B.y + fs + k * fs * 1.35, { font: 'ui', size: fs, color: PENCIL() }));
        return;
      }
      const W = show.working, i = show.i, at = st.showAt;
      const head = `Claim ${i + 1}: the working`;
      let fs = docked ? 42 : Math.round(UI.text(26)), lines;
      for (let k = 0; k < 6; k++) {
        lines = W.lines.flatMap((l, j) => UI._wrap(l, B.w, uiF(fs, j === W.lines.length - 1 ? 700 : 400)).map((t, m) => [t, j, m]));
        if ((lines.length + 1) * fs * 1.4 + 10 <= B.h) break;
        fs = Math.floor(fs * 0.9);
      }
      const lh = fs * 1.4;
      NB.write(head, B.x, B.y + fs * 0.7, { font: 'ui', weight: 700, size: fs, color: PENCIL(), t0: at, dur: 0.25 });
      lines.forEach(([t, j, m], k) => {
        const yy = B.y + fs * 0.7 + (k + 1) * lh + 6, lastLine = j === W.lines.length - 1;
        if (lastLine && m === 0) NB.highlight(B.x - 4, yy - fs * 0.7, tw(t, uiF(fs, 700)) + 8, fs * 1.4, { t0: at + 0.4, dur: 0.25, alpha: 0.7 });
        NB.write(t, B.x, yy, { font: 'ui', weight: lastLine ? 700 : 400, size: fs, color: INK(), t0: at + 0.1 + k * 0.1, dur: 0.3 });
      });
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    drawSpot(L) {
      const X = L.panelX, W = 1880 - X, fs = UI.text(30), hs = UI.text(44);
      const body = ['Is every claim true?', 'Check each claim against the drawing: count the outcomes, then P = favorable ÷ total.', 'Tap the wrong claim, or All correct.'];
      const wrapped = body.flatMap((l, i) => UI._wrap(l, W - 70, uiF(fs, i ? 400 : 700)).map(t => [t, i]));
      const head = hs * 1.8, h = head + wrapped.length * fs * 1.35 + 24;
      const hs0 = UI.hudSlot(0), top = Math.max(205, hs0.y + hs0.r + 30);
      NB.indexCard(X, top, W, h, { header: head, pitch: fs * 1.35 });
      NB.write(`Poster ${this.bi + 1} of ${this.n}`, X + 34, top + head * 0.5, { size: hs, weight: 700, t0: this.boardAt });
      wrapped.forEach(([t, i], k) => NB.write(t, X + 34, top + head + (k + 0.5) * fs * 1.35 + 6, { font: 'ui', weight: i ? 400 : 700, size: fs, t0: this.boardAt + 0.2 + k * 0.08, dur: 0.25 }));
      const bh = UI.tap(92), by = top + h + 26 + bh / 2;
      if (Game.time - this.boardAt > 0.5 && UI.button('fp-legit', (UI.keys ? 'A  ' : '') + 'All correct ✓', X + W / 2, by, { w: Math.min(W - 40, 520), h: 92, secondary: true })) this.legit();
      const y = by + bh / 2 + 34, ns = UI.text(30);
      if (this.say && T - this.say.at < 7) {
        const L2 = UI._wrap(this.say.text, W - 40, handF(ns));
        L2.forEach((t, k) => NB.note(t, X + 20, y + k * ns * 1.25, -0.01, { t0: this.say.at + k * 0.3, size: ns, dur: 0.35 }));
      }
    }
    valueCard(L) {
      const X = L.panelX, W = 1880 - X, q = this.inst, qs = UI.text(38), os = UI.text(40);
      const intro = this.revealed ? (this.R.legit ? 'It was all correct.' : 'Ms. Rivera circled it.') : this.R.legit ? 'Right: all correct!' : 'Got it: that claim is wrong.';
      const hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 16;
      const lp = last(this.picks), why = lp != null && lp !== q.answerIndex ? q.why[lp] : '';
      const rows = q.options.map(() => Math.max(UI.tap(92) + 4, os * 1.9));
      let C;
      for (const [withHint, k] of [[true, 1], [false, 1], [false, 0.85], [false, 0.72]]) {
        const is = UI.text(30) * k, iL = UI._wrap(intro, W - 80, handF(is, 700)), qL = UI._wrap(q.text, W - 80, uiF(qs * k, 700));
        const head = 40 + iL.length * is * 1.3 + qL.length * qs * k * 1.25;
        const ws = UI.text(30) * k, whyL = why ? UI._wrap(why, W - 80, handF(ws)) : [];
        const hs = UI.text(26) * k, hintL = withHint ? UI._wrap('P = favorable ÷ total. Tap ÷ on a claim to see the count.', W - 80, uiF(hs)) : [];
        const h = head + rows.reduce((a, b) => a + b, 0) + 20 + hintL.length * hs * 1.3 + 10 + whyL.length * ws * 1.3 + (whyL.length ? 16 : 0);
        C = { X, W, q, qs: qs * k, os, qL, head, rows, whyL, ws, hintL, hs, h, top: Math.max(top0, Math.min(205, 1070 - h)), iL, is };
        if (top0 + h <= 1070) break;
      }
      return C;
    }
    drawValue(L) {
      const C = this.valueCard(L), { X, W, q, qs, os, qL, head, rows, whyL, ws, hintL, hs, top, iL, is, h } = C, t0 = this.pAt;
      if (T < t0) return;
      NB.indexCard(X, top, W, h, { header: head, pitch: 1e4 });
      iL.forEach((l, i) => NB.note(l, X + 34, top + 20 + (i + 0.5) * is * 1.3, 0, { t0, size: is, weight: 700, dur: 0.3 }));
      const qy = top + 20 + iL.length * is * 1.3;
      qL.forEach((l, i) => NB.write(l, X + 34, qy + (i + 0.5) * qs * 1.25, { font: 'ui', weight: 700, size: qs, t0: t0 + 0.2 + i * 0.15, dur: 0.3 }));
      let y = top + head;
      const lp = last(this.picks);
      q.options.forEach((o, i) => {
        const rh = rows[i], cy = y + rh / 2, kx = X + 60, crossed = this.picks.includes(i);
        g.save(); g.fillStyle = NB.PAL.cardLine; g.fillRect(X, y + rh - 1, W, 1.5); g.restore();
        const hit = !crossed ? UI._hit('fp-opt' + this.bi + '_' + i, X + W / 2, cy, W - 20, rh - 2) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(X + 12, y + 6, W - 24, rh - 12); g.restore(); }
        g.save(); g.lineWidth = 3; g.strokeStyle = INK(); g.strokeRect(kx - 20, cy - 20, 40, 40);
        g.font = uiF(24, 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), kx, cy + 1); g.restore();
        const mw = NB.write(optText(q, i), kx + 50, cy, { size: os, weight: 700, color: crossed ? PENCIL() : INK(), t0: t0 + 0.2 + i * 0.1, dur: 0.25 });
        if (crossed) NB.strike(kx + 40, cy - 16, kx + 60 + mw, cy + 16, { t0: i === lp ? this.pickedAt : -1e9, w: 5 });
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      hintL.forEach((l, k) => NB.write(l, X + 34, y + 20 + (k + 0.5) * hs * 1.3, { font: 'ui', size: hs, color: PENCIL(), t0: t0 + 0.5 + k * 0.2, dur: 0.3 }));
      y += 20 + hintL.length * hs * 1.3 + 10;
      whyL.forEach((l, k) => NB.note(l, X + 34, y + (k + 0.5) * ws * 1.3, 0, { t0: this.pickedAt + 0.3 + k * 0.3, size: ws, dur: 0.35 }));
    }
    /* Rivera's card: the wrong claim, its working, the item's explain, this poster's stars */
    fixLines() {
      const R = this.R, res = this.results[this.bi] || { stars: 0, claimStar: 0, valueStar: 0 }, out = [];
      if (R.legit) {
        out.push(['head', res.claimStar ? 'All correct!' : 'It was all correct.']);
        out.push(['rule', this.inst.explain || R.P.explain || '']);
      } else {
        const c = R.P.claims[R.bad];
        out.push(['head', "Ms. Rivera's red pen"]);
        out.push(['bad', '✗ ' + c.text]);
        c.working.lines.forEach(l => out.push(['work', l]));
        out.push(['rule', this.inst.explain || R.P.explain || '']);
      }
      out.push(['stars', `+${res.stars} star${res.stars === 1 ? '' : 's'} (${R.legit ? 'all correct' : 'claim'} ${res.claimStar}, value ${res.valueStar})`]);
      return out;
    }
    fixCard(L) {
      const key = this.bi + '|' + Math.round(UI.text(28)) + '|' + L.panelX;
      if (this._fc && this._fc.key === key) return this._fc;
      const F = this.fixLines(), bh = UI.tap(92), hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 24, X = L.panelX, W = 1880 - X;
      let C;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64]) {
        const S = { head: [UI.text(44) * k, handF(UI.text(44) * k, 700)], bad: [UI.text(30) * k, handF(UI.text(30) * k, 700)], work: [UI.text(28) * k, uiF(UI.text(28) * k)],
          rule: [UI.text(32) * k, handF(UI.text(32) * k)], stars: [UI.text(34) * k, handF(UI.text(34) * k, 700)] };
        const lh = kk => S[kk][0] * 1.32;
        const rows = F.flatMap(([kk, t]) => UI._wrap(t, W - 70, S[kk][1]).map((l, j) => [kk, l, j > 0]));
        const h = rows.reduce((a, [kk]) => a + lh(kk), 0) + 36;
        C = { key, X, W, rows, h, S, lh, bh, top: Math.max(96, Math.min(top0, 1080 - 14 - bh - 22 - h)) };
        if (top0 + h + 22 + bh + 14 <= 1080) break;
      }
      return (this._fc = C);
    }
    drawFix(L) {
      const C = this.fixCard(L), { X, W, rows, h, S, lh, bh, top } = C, t0 = this.fixAt + 0.2;
      NB.indexCard(X, top, W, h, { header: rows.filter(([k]) => k === 'head').length * lh('head') + 20, pitch: 1e4 });
      let y = top + 18, tt = t0, wasHead = true;
      rows.forEach(([k, l]) => {
        if (wasHead && k !== 'head') { y += 6; wasHead = false; }
        const s = S[k][0], cy = y + lh(k) / 2 + (k === 'head' ? 2 : 8);
        const o = { size: s, t0: tt, dur: Math.max(0.2, Math.min(0.5, l.length * 0.02)) };
        if (k === 'head') NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        else if (k === 'rule') NB.note(l, X + 34, cy, 0, o);
        else if (k === 'bad') NB.write(l, X + 34, cy, { ...o, weight: 700, color: PENCIL() });
        else if (k === 'work') NB.write(l, X + 34, cy, { ...o, font: 'ui', color: INK() });
        else NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        tt += o.dur * 0.6; y += lh(k);
      });
      const label = this.bi < this.n - 1 ? 'Next poster ▶' : 'See my stars ▶';
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('fp-next', label, X + W / 2, top + h + 22 + bh / 2, { w: Math.min(W - 40, 440), h: 92, primary: true })) this.next();
    }

    /* ---------------- portrait: the poster is the picture; everything else in the dock ---------------- */
    drawDock(L) {
      const R = this.R, q = this.inst, id = this.attempt + '_' + this.bi, n = R.n, P = R.P;
      Dock.head(`Poster ${this.bi + 1} of ${this.n}`, `${this.stars} of ${this.of} stars`);
      const opts = P.claims.map(c => c.text);
      const notes = P.claims.map((c, i) => (this.marks[i] ? 'fine: ' + oneLine(c) : ''));
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('fp-how', ['Check each claim against the drawing: P = favorable ÷ total. Tap the wrong claim, or All correct.'], { cls: 'note' });
        if (this.phase === 'spot') {
          Dock.choices('fp-claims' + id, { prompt: 'The fine print', options: opts, state: P.claims.map((c, i) => (this.marks[i] ? 'faded' : '')), notes, onChoose: i => this.tapClaim(i) });
          Dock.buttons('fp-legit' + id, [{ label: 'All correct ✓', wide: true, onClick: () => this.legit() }]);
          if (this.say) Dock.text('fp-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
          this.dockChecks(id, n);
        }
        return;
      }
      if (this.phase === 'value') {
        const found = this.found >= 0 ? (this.revealed ? 'Ms. Rivera circled: ' : 'You circled: ') + opts[this.found] : (this.revealed ? 'It was all correct.' : 'Right: all correct!');
        Dock.text('fp-found' + id, [found], { cls: this.revealed ? 'note' : 'right' });
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        Dock.choices('fp-val' + id, { prompt: q.text, options: q.options.map((o, i) => optText(q, i)), state: st, notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        this.dockChecks(id, n);
        return;
      }
      // fix
      const F = this.fixLines();
      Dock.choices('fp-val' + id, { prompt: q.text, options: q.options.map((o, i) => optText(q, i)), state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('fp-fix' + id, F[0][1]);
      Dock.list('fp-work' + id, P.claims.map((c, i) => ({ badge: String(i + 1), color: i === R.bad ? NB.PAL.redpen : NB.PAL.ink,
        lines: i === R.bad ? [`${c.text}  ✗`, `honest: ${c.honestText}`] : [c.text, oneLine(c)] })));
      Dock.text('fp-rule' + id, [this.inst.explain || P.explain || ''], { cls: 'note' });
      Dock.text('fp-stars' + id, F.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next poster ▶' : 'See my stars ▶', label: 'next', aria: 'next poster', go: true, onClick: () => this.next() });
    }
    /* portrait: a ÷ per claim (free: it only shows that claim's working) */
    dockChecks(id, n) {
      Dock.text('fp-chkq' + id, ['Check a claim (free): tap its ÷'], { cls: 'small' });
      Dock.buttons('fp-chkb' + id, Array.from({ length: n }, (v, i) => ({ label: '÷ ' + (i + 1), onClick: () => this.check(i) })));
      if (this.lastCheck >= 0) Dock.text('fp-chkt' + id + '_' + this.lastCheck, [`Claim ${this.lastCheck + 1}:`, ...this.working(this.lastCheck).lines], { cls: 'note', reveal: true });
    }

    drawIntro(docked) {
      const t0 = this.enteredAt, n = this.n, pass = this.passStars, of = this.of;
      const lines = ["Jay wrote the fine print on Ms. Rivera's prize posters. Uh-oh.", `${n} posters. All but one have a claim that's wrong.`,
        'Count the outcomes. Tap the wrong claim, then find the real value.', `${pass} of ${of} stars for the stamp.`];
      // a sample honest poster: the video's wheel, every number from the model
      if (!this._demo) {
        const W = Chance.VIDEO.wheel, e = Chance.exp(W), t = ev => Chance.text(Chance.P(e, ev));
        const item = { id: 'demo', title: 'SPIN TO WIN', cheat: 'legit', exp: W, claims: [
          { text: `P(FREE SOCKS) = ${t('label:FREE SOCKS')}`, event: 'label:FREE SOCKS' },
          { text: `P(10% OFF) = ${t('label:10% OFF')}`, event: 'label:10% OFF' },
          { text: `P(TRY AGAIN) = ${t('label:TRY AGAIN')}`, event: 'label:TRY AGAIN' }], q: '', formula: '1', format: 'frac' };
        const P = Chance.poster(item, {});
        this._demo = { P, src: item, legit: true, bad: -1, n: 3, jar: false };
      }
      const R = this._demo;
      const st = { docked, spot: false, live: false, marks: {}, checked: {}, found: -1, fix: false, bi: -1, keys: false, show: -1, chips: false };
      if (docked) {
        this.drawPoster(R, posterLay(R, 'd'), st);
        Dock.heading('fp-intro', 'Fine Print', { cls: 'big' });
        Dock.text('fp-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start checking prize posters', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      const L = posterLay(R, 'm');
      g.save(); g.translate(20, 0); g.translate(180, 205); g.scale(0.82, 0.82); g.translate(-180, -205); this.drawPoster(R, L, st); g.restore();
      NB.title('Fine Print', 1400, 250, { size: 110, t0: t0 + 0.2, hi: 'Print' });
      const fs = UI.text(36);
      let y = 400;
      lines.forEach((l, i) => UI._wrap(l, 820, uiF(fs, i === 3 ? 700 : 400)).forEach(tx => { NB.write(tx, 1400, y, { font: 'ui', size: fs, weight: i === 3 ? 700 : 400, align: 'center', t0: t0 + 0.5 + i * 0.3, dur: 0.45 }); y += fs * 1.45; }));
      if (this.t > 0.8 && UI.button('fp-start', 'Start ▶', 1400, Math.min(960, y + 90), { w: 420, h: 110, primary: true })) this.begin();
      if (this.t > 1.2) NB.write(UI.hint('(or press Enter)', ''), 1400, Math.min(960, y + 90) + 80, { font: 'ui', size: UI.text(26), color: PENCIL(), align: 'center' });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0 }, it = Chance.source(inst.item); return { r, text: `${i + 1}. ${it.title}: ${SHORT[it.cheat] || it.cheat}` }; });
      const word = this.passed ? 'AUDITED' : 'TRY AGAIN';
      if (docked) {
        NB.title(`${s} / ${of}`, 960, 380, { size: 200, t0: this.endAt + 0.1, hi: String(s) });
        UI.stamp(word, 960, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6 });
        Dock.heading('fp-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('fp-end', [this.passed ? 'Audited! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New posters?`]);
        Dock.list('fp-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? NB.PAL.ink : NB.PAL.redpen, lines: [x.text.replace(/^\d+\. /, ''), `${x.r.stars} of 2 stars`] })));
        Dock.text('fp-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('fp-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      NB.title(`${s} / ${of}`, 640, 250, { size: 140, t0: this.endAt + 0.1, hi: String(s) });
      UI.stamp(word, 1330, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1 });
      const fs = UI.text(34), rh = Math.max(66, fs * 1.4), bh = UI.tap(100), ch = rows.length * rh + 50;
      const cardTop = Math.min(350, 1080 - 40 - bh - 70 - ch - 20), top = cardTop + 25 + rh / 2;
      NB.indexCard(260, cardTop, 1400, ch, { header: 25, pitch: rh });
      rows.forEach((x, i) => {
        const y = top + i * rh;
        NB.write(x.text, 320, y, { font: 'ui', size: fs, t0: this.endAt + 0.3 + i * 0.1, dur: 0.3 });
        NB.write(`${x.r.stars} of 2`, 1580, y, { size: fs * 1.15, weight: 700, color: x.r.stars === 2 ? INK() : RED(), align: 'right', t0: this.endAt + 0.4 + i * 0.1 });
        if (x.r.stars === 2) NB.tick(1620, y, { s: 0.9, t0: this.endAt + 0.5 + i * 0.1 });
      });
      const py = cardTop + ch + 40, by = Math.min(1080 - 40 - bh / 2, py + 40 + bh / 2);
      NB.write(this.passed ? 'Audited! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 960, py, { font: 'ui', size: UI.text(34), weight: 700, align: 'center', t0: this.endAt + 0.8, dur: 0.4 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('fp-end' + i, b.label, 640 + i * 520, by, { w: 480, h: 100, primary: i === 0, secondary: i > 0 })) b.go(); });
      NB.write(`quiz code ${this.seed}`, 1860, by + bh / 2 + 24, { font: 'mono', size: UI.text(22), color: PENCIL(), align: 'right', t0: this.endAt + 0.6 });
    }
  }
  Print.STEP = STEP; Print.pool = pool; Print.round = round; Print.posterLay = posterLay; Print.valueAt = valueAt;
  /* the live scene's solution (tests) */
  Object.defineProperty(Print, 'solution', { get() { const s = Game.current(); return s instanceof Print ? s.solution : null; } });
  /* the tap rects of the live scene's claims on the canvas (tests tap them): {i, x, y, w, h} centred */
  Print.claims = () => {
    const s = Game.current();
    if (!(s instanceof Print) || !s.R) return [];
    return s.lay(UI.docked()).rows.map(r => ({ i: r.i, x: r.cx, y: r.cy, w: r.w, h: r.h }));
  };
  Levels.print = Print;
})();
